import React, { Component, createRef, useEffect, useState } from "react";
import i18n from "i18next";
import {
  HiArrowPath,
  HiArrowUturnLeft,
  HiClock,
  HiArrowDownTray,
  HiVideoCamera,
  HiEyeSlash,
  HiEye,
  HiPlay,
  HiStop,
  HiMagnifyingGlass,
  HiExclamationTriangle,
  HiLanguage,
} from "react-icons/hi2";
import { authedFetch } from "../../../../common/http_service";
import { approveJob } from "../../../../common/server_utils.es6";
import { getStore } from "../../../../common/environment_config";
import { credentialsFor } from "../../../../common/utils";
import { connectVideoExportSocket } from "../../../utils/videoExportSocket";

/* eslint-disable react/prop-types */

const apiBase = () => (window.ideaLibraryRootUrl || "").replace(/\/$/, "");

// Snapshot poll used only when the socket can't connect (see _attachSocket).
const SNAPSHOT_POLL_MS = 5000;

// mw appends `_t=<now>` to every media URL in a snapshot; compare without it.
const stripCacheBuster = (url) =>
  url ? url.replace(/([?&])_t=\d+(&|$)/, (m, lead, tail) => (tail ? lead : "")) : url;

// Raw backend status enum → translated label. Mirrors console's
// translateStatus() so both modals show a translated word instead of the
// literal English enum value regardless of the selected UI language.
const STATUS_KEYS = {
  queued: "status_queued",
  rendering: "status_rendering",
  uploading: "status_uploading",
  completed: "status_completed",
  failed: "status_failed",
  cancelled: "status_cancelled",
};
const translateStatus = (status) =>
  i18n.t(`video_export.${STATUS_KEYS[status] || "status_queued"}`) || status;

/**
 * Sahne adları doğrudan Redux store'dan: `viewToolbarsById[id].viewName`
 * editörün tek doğru kaynağı — sahne indeksindeki satır içi yeniden
 * adlandırma da sağ panelin "Sahne Adı" alanı da bunu yazıyor.
 * (`navItemsById[id].title/name` yalnızca içe aktarılmış eski state'ler
 * için yedek; reducer bunları hiç doldurmuyor.)
 *
 * API'nin döndürdüğü `scene.title` yerine BU tercih ediliyor: job snapshot'ı
 * adları export başladığı anda donduruyor, sonradan yeniden adlandırılan bir
 * sahne "Sahneleri Güncelle"ye basılana kadar eski adıyla görünürdü.
 */
const readLocalSceneTitles = () => {
  try {
    const store = getStore();
    const present = (store && store.getState()?.undoGroup?.present) || {};
    const viewToolbarsById = present.viewToolbarsById || {};
    const navItemsById = present.navItemsById || {};
    const titles = {};
    Object.keys(viewToolbarsById).forEach((id) => {
      const name = String(viewToolbarsById[id]?.viewName || "").trim();
      if (name) titles[id] = name;
    });
    Object.keys(navItemsById).forEach((id) => {
      if (titles[id]) return;
      const nav = navItemsById[id];
      const name = String(nav?.title || nav?.name || "").trim();
      if (name) titles[id] = name;
    });
    return titles;
  } catch (err) {
    console.warn("[VideoExportTabV2] failed to read local scene titles:", err);
    return {};
  }
};

const PHASE = {
  CHECKING: "checking", // on mount, looking for an existing job
  IDLE: "idle", // no job — show the start CTA
  STARTING: "starting", // POST in flight
  RUNNING: "running", // job alive, scenes rendering
  AWAITING_MERGE: "awaiting-merge", // all scenes done, manual merge waiting
  MERGING: "merging",
  DONE: "done",
  FAILED: "failed",
};

/**
 * Video Export v2 popup — manual-merge layout.
 *
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ [Stop] [Regenerate All] [Merge All]                          │  toolbar
 *   ├─────────────────────────────────────────────────────────────┤
 *   │  Scene strip (horizontal scroll of small tiles)              │  ~20% h
 *   │  ┌──┐┌──┐┌──┐┌──┐┌──┐┌──┐┌──┐┌──┐                            │
 *   │  │ 1││ 2││ 3││ 4││ 5││ 6││ 7││ 8│                            │
 *   │  └──┘└──┘└──┘└──┘└──┘└──┘└──┘└──┘                            │
 *   ├─────────────────────────────────────────────────────────────┤
 *   │                                                              │
 *   │     BIG PREVIEW (selected scene's video, or merged)          │  ~80% h
 *   │                                                              │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * - Clicking a tile selects it; the big player swaps to that scene's URL.
 * - When merge completes the player auto-switches to merged.mp4.
 * - Regenerate button next to the player acts on the selected scene.
 * - Live updates via Socket.IO; cold-start GET /:jobId fills the snapshot.
 */
export default class VideoExportTabV2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      phase: PHASE.CHECKING,
      jobId: null,
      scenes: [],
      mergedVideoUrl: null,
      mergeStatus: null,
      selectedNavItemId: null, // which tile's video plays in the big preview
      errorMessage: "",
      // Manual order + skip flags for the merge step. `order` is an array
      // of navItemIds that overrides the server-default scene order. Empty
      // array means "use server order". `skipMerge` is a Set of navItemIds
      // the user has marked "Don't Merge" — they stay visible in the strip
      // but are filtered out at merge time. Both reset only on tear-down,
      // so live scene:lifecycle events don't clobber the user's edits.
      order: [],
      skipMerge: new Set(),
      // Native HTML5 drag-and-drop state — which tile is currently being
      // dragged. Stored in state only so the strip can render a visual
      // "dragging" affordance; not persisted.
      draggingNavItemId: null,
      // Merge-history dropdown — fetched lazily when the user opens it.
      // Each entry is { id, url, jobId, durationSec, fileSize, createdAt }.
      mergeHistory: null,
      mergeHistoryOpen: false,
      mergeHistoryLoading: false,
      // True while the Reload Scenes button is in flight; disables the
      // button + swaps in a spinner icon so the user can't double-fire.
      reloadingScenes: false,
      // True while the Recover Old Videos probe is running. Same
      // double-fire guard pattern.
      recoveringVideos: false,
      // True while the Clear (delete-all-records-for-training) request is
      // in flight. Mirrors console's "Temizle" button.
      clearing: false,
      // ── Video translation (dubbing) ────────────────────────────────────
      // Languages we can dub INTO — /video-translation/languages, i.e. the
      // distinct languages of the active TTS voice catalog.
      translateLanguages: [],
      // The one entry point for everything dubbing-related — see LanguageModal.
      languageModalOpen: false,
      // Playable videos for this training: the original merge + one entry per
      // dubbed language (/video-translation/by-training/:tid). Listed inside
      // the language modal; picking one routes it into the big preview.
      videoVariants: [],
      // idealStudioFiles.id of the entry currently in the big preview, or
      // null while the preview is showing a live merge rather than a stored
      // variant.
      selectedVariantId: null,
      // In-flight dub: { jobId, language, progress, message }.
      translateJob: null,
      translateError: null,

      // Subtitle language selection — list fetched from /studio/subtitle/:tid/languages
      subtitleLanguages: [],
      selectedSubtitleLang: "",
      // Which download button is currently in flight ("merged",
      // "scene:<id>", "history:<id>") — drives a per-button spinner so a
      // slow response doesn't read as an unresponsive button inviting a
      // re-click.
      downloadingKey: null,
      // Controls the "Başla" start-confirm overlay (language picker
      // shown before the very first export kicks off).
      showStartModal: false,
      // navItemId of a scene pending single-scene regenerate confirmation
      // (language-picker overlay, same one "Başla" uses) — null when closed.
      regenerateModalNavItemId: null,
    };
    this._unsubscribeSocket = null;
    // Ref bound to whichever preview <video> is currently rendered
    // (scene preview or merged-video preview). The skip buttons and the
    // keyboard ArrowLeft / ArrowRight handler read this to nudge the
    // playhead ±5s without touching the native seek bar.
    this._previewVideoRef = createRef();
  }

  componentDidMount() {
    this._lookupExistingJob();
    this._fetchSubtitleLanguages();
    this._fetchTranslateLanguages();
    this._fetchVideoVariants();
  }

  /** Target languages for dubbing. Absent list → the button stays hidden. */
  async _fetchTranslateLanguages() {
    try {
      const res = await authedFetch(`${apiBase()}/api/video-translation/languages`);
      if (!res.ok) return;
      const data = await res.json();
      this.setState({ translateLanguages: data?.languages ?? [] });
    } catch {
      /* dubbing is optional — a missing list just hides the button */
    }
  }

  /**
   * Refresh the video select: the newest merged export plus the newest dub
   * per language. Called on mount, after a dub finishes, and after a merge
   * lands (a fresh merge becomes the new "Orijinal" entry).
   */
  async _fetchVideoVariants() {
    const tid = this._trainingId();
    if (!tid) return;
    try {
      const res = await authedFetch(`${apiBase()}/api/video-translation/by-training/${tid}`);
      if (!res.ok) return;
      const list = await res.json();
      this.setState({ videoVariants: Array.isArray(list) ? list : [] });
    } catch {
      /* non-fatal — the select just stays as-is */
    }
  }

  async _fetchSubtitleLanguages() {
    const tid = this._trainingId();
    if (!tid) return;
    try {
      const res = await authedFetch(`${apiBase()}/api/studio/subtitle/${tid}/languages`);
      if (!res.ok) return;
      const data = await res.json();
      const langs = data?.availableLanguages ?? [];
      this.setState({ subtitleLanguages: langs });
    } catch {
      /* subtitle languages are optional — ignore errors */
    }
  }

  componentWillUnmount() {
    this._teardown();
    // Not part of _teardown: that also runs on socket re-attach and on
    // "Yeniden Başla", and an in-flight dub must keep being polled through
    // both. Only an actual unmount ends the poll.
    this._clearTranslationPoll();
  }

  /**
   * Nudge the preview video's playhead by `secs` seconds (negative =
   * back). Clamped to [0, duration]. No-op when no preview is mounted
   * or when the underlying mp4 hasn't reached HAVE_METADATA yet
   * (duration is NaN before then — guarded so we don't seek into a
   * NaN ceiling).
   */
  _skipPreview = (secs) => {
    const v = this._previewVideoRef.current;
    if (!v) return;
    const dur = Number.isFinite(v.duration) ? v.duration : 0;
    const target = Math.max(0, Math.min(dur || Infinity, (v.currentTime || 0) + secs));
    try {
      v.currentTime = target;
    } catch {
      /* unseekable source (e.g. moov-at-end mp4) — swallow */
    }
  };

  _downloadWithLoading = async (key, url, filename) => {
    this.setState({ downloadingKey: key });
    try {
      await triggerDownload(url, filename);
    } finally {
      this.setState({ downloadingKey: null });
    }
  };

  _onPreviewKeyDown = (e) => {
    // Don't intercept typing inside form controls that might briefly
    // bubble up here (none currently exist inside the preview wrapper,
    // but cheap to guard).
    const tag = (e.target && e.target.tagName) || "";
    if (tag === "INPUT" || tag === "TEXTAREA") return;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      this._skipPreview(-5);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      this._skipPreview(5);
    }
  };

  /**
   * Before any export job exists, read the training's current scene list
   * straight out of the Redux store (undoGroup.present.navItemsIds/
   * navItemsById) so the strip isn't empty on first open — the user can
   * then mark "Don't Merge" on scenes before ever clicking "Başla" (see
   * _startExport, which honors these pre-marked exclusions). Matches the
   * "pa-" id-prefix filter SaveRequiredGate already uses to distinguish
   * real scene pages from the reducer's synthetic root/section entries.
   * Placeholder scenes have no video yet — they render as plain numbered
   * "queued" tiles until a real export job replaces them with live data.
   */
  _loadLocalScenesFromStore() {
    try {
      const store = getStore();
      const state = store && store.getState();
      const navItemsIds = state?.undoGroup?.present?.navItemsIds || [];
      const titles = readLocalSceneTitles();
      return navItemsIds
        .filter((id) => typeof id === "string" && id.startsWith("pa-"))
        .map((id) => ({ navItemId: id, status: "queued", title: titles[id] }));
    } catch (err) {
      console.warn("[VideoExportTabV2] failed to read local scene list:", err);
      return [];
    }
  }

  /**
   * Resume gate: on open, ask the middleware if there's already an
   * export running for this training. If yes, attach the socket and
   * paint whatever scenes are already done; if no, show the IDLE CTA
   * pre-populated with the training's scene list (see
   * _loadLocalScenesFromStore). This is what makes refreshing the popup
   * or re-opening it mid-render not lose live progress.
   */
  async _lookupExistingJob() {
    const tid = this._trainingId();
    if (!tid) {
      this.setState({ phase: PHASE.IDLE, scenes: this._loadLocalScenesFromStore() });
      return;
    }
    try {
      const res = await authedFetch(`${apiBase()}/api/video-export-v2/by-training/${tid}`);
      if (!res.ok) throw new Error(`by-training → ${res.status}`);
      const { jobId, snapshot } = await res.json();
      if (!jobId || !snapshot) {
        this.setState({ phase: PHASE.IDLE, scenes: this._loadLocalScenesFromStore() });
        return;
      }
      const allDone =
        snapshot.scenes.length > 0 && snapshot.scenes.every((s) => s.status === "completed");
      const firstCompleted = snapshot.scenes.find((s) => s.status === "completed" && s.videoUrl);
      await this._attachSocket(jobId);
      this.setState(
        {
          jobId,
          scenes: snapshot.scenes,
          mergedVideoUrl: snapshot.mergedVideoUrl ?? null,
          selectedNavItemId:
            (snapshot.mergedVideoUrl && "__MERGED__") ||
            firstCompleted?.navItemId ||
            snapshot.scenes[0]?.navItemId ||
            null,
          phase: snapshot.mergedVideoUrl
            ? PHASE.DONE
            : allDone
              ? PHASE.AWAITING_MERGE
              : PHASE.RUNNING,
        },
        // Auto-sync the strip with the training's current scene list
        // AFTER the resume setState lands (otherwise _reloadScenes
        // reads `this.state.jobId` before React applies it and bails).
        // Cheap, idempotent — `reloadScenes` is strictly additive on
        // the backend (preserves rendered videos + the merged output)
        // and is a no-op when the snapshot already contains every
        // navItem. Fixes two failure modes:
        //   1. Resuming a job whose scene placeholders were soft-
        //      deleted (regenerate, reconciler-zombied) so the DB
        //      rebuild returns an empty strip with only the merged
        //      tile.
        //   2. A new scene was added in the editor while the popup
        //      was closed.
        // Fire-and-forget; failure logs through `_reloadScenes`.
        () => {
          this._reloadScenes();
        }
      );
    } catch (err) {
      // Network or 4xx — degrade to IDLE so the user can still kick off a new export.
      console.warn("[VideoExportTabV2] resume lookup failed:", err);
      this.setState({ phase: PHASE.IDLE, scenes: this._loadLocalScenesFromStore() });
    }
  }

  _teardown() {
    // Invalidates an attach still waiting on the socket (see _attachSocket).
    this._attachSeq = (this._attachSeq || 0) + 1;
    this._stopSnapshotPoll();
    if (this._unsubscribeSocket) {
      this._unsubscribeSocket();
      this._unsubscribeSocket = null;
    }
  }

  _trainingId() {
    return this.props.trainingId || window.idealStudioTrainingId;
  }

  async _startExport(forceRestart = false) {
    // Captured before the STARTING setState below — these reflect
    // whatever the user pre-marked "Don't Merge" on while the strip was
    // still showing local (pre-job) placeholders.
    const { selectedSubtitleLang, scenes: preStartScenes, skipMerge } = this.state;
    // Scenes the user excluded pre-start are left out of the render
    // request entirely (the backend's start endpoint accepts an optional
    // navItemIds subset — "omit to render every page"), instead of
    // rendering them and only excluding them from the merge afterward.
    const navItemIds =
      preStartScenes.length > 0
        ? preStartScenes.filter((s) => !skipMerge.has(s.navItemId)).map((s) => s.navItemId)
        : undefined;
    this.setState({ phase: PHASE.STARTING, errorMessage: "" });
    try {
      const res = await authedFetch(`${apiBase()}/api/video-export-v2`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          trainingId: this._trainingId(),
          forceRestart,
          // Visor arayüz dili. Kaydedilmiş kurs state'i dili taşımıyor
          // (globalConfig.__editorLanguage yalnızca editör önizlemesinde
          // ekleniyor), o yüzden export isteği editörün o anki dilini
          // açıkça bildirir; worker bunu sahne URL'ine &lng= olarak ekler.
          language: i18n.language,
          ...(selectedSubtitleLang ? { subtitleLanguage: selectedSubtitleLang } : {}),
          ...(navItemIds && navItemIds.length ? { navItemIds } : {}),
        }),
      });
      if (!res.ok) throw new Error(`POST /api/video-export-v2 → ${res.status}`);
      const { jobId } = await res.json();
      await this._attachSocket(jobId);
      const snapRes = await authedFetch(`${apiBase()}/api/video-export-v2/${jobId}`);
      if (snapRes.ok) {
        const snap = await snapRes.json();
        const backendScenes = snap.scenes || [];
        // Scenes excluded from the request above never got a job entry —
        // keep them visible (still "queued" + still marked skipped) in
        // their original position instead of letting them disappear,
        // so the strip still reflects "every scene in the training",
        // just with some permanently sitting out until un-skipped and
        // picked up by a later "Sahneleri Güncelle".
        const backendByNavId = new Map(backendScenes.map((s) => [s.navItemId, s]));
        const merged = preStartScenes.map((s) => backendByNavId.get(s.navItemId) || s);
        for (const s of backendScenes) {
          if (!preStartScenes.some((local) => local.navItemId === s.navItemId)) merged.push(s);
        }
        this.setState({
          jobId,
          phase: PHASE.RUNNING,
          scenes: merged.length ? merged : backendScenes,
          selectedNavItemId: backendScenes[0]?.navItemId ?? merged[0]?.navItemId ?? null,
        });
      } else {
        this.setState({
          jobId,
          phase: PHASE.RUNNING,
        });
      }
    } catch (err) {
      this.setState({ phase: PHASE.FAILED, errorMessage: err.message });
    }
  }

  /**
   * "Başla" toolbar button (IDLE phase only) — opens the start-confirm
   * overlay instead of kicking off the export immediately, so the user
   * picks a subtitle language first (mirrors the language select that
   * used to sit on the now-removed IDLE landing screen).
   */
  _openStartModal = () => {
    this.setState({ showStartModal: true });
  };

  _closeStartModal = () => {
    this.setState({ showStartModal: false });
  };

  _confirmStart = () => {
    this.setState({ showStartModal: false });
    this._startExport(false);
  };

  /**
   * Per-scene "Yeniden Üret" — opens the same language-picker overlay as
   * "Başla" instead of silently regenerating with whatever the toolbar's
   * language dropdown happens to be set to, so the choice is explicit for
   * this one scene too.
   */
  _openRegenerateModal = (navItemId) => {
    this.setState({ regenerateModalNavItemId: navItemId });
  };

  _closeRegenerateModal = () => {
    this.setState({ regenerateModalNavItemId: null });
  };

  _confirmRegenerateScene = () => {
    const navItemId = this.state.regenerateModalNavItemId;
    this.setState({ regenerateModalNavItemId: null });
    if (navItemId) this._regenerateScene(navItemId);
  };

  /**
   * Subscribes to live events without blocking the caller. This used to be
   * awaited before the snapshot was painted, so wherever WebSocket can't
   * connect (some corporate proxies block it) the popup sat on its loading
   * state forever. Now the snapshot paints right away and, if the socket
   * fails, progress keeps arriving by polling GET /:jobId instead.
   */
  _attachSocket(jobId) {
    this._teardown();
    const attachSeq = this._attachSeq;
    connectVideoExportSocket({
      jobId,
      onEvent: (event) => this._applyEvent(event),
    })
      .then((unsubscribe) => {
        if (attachSeq !== this._attachSeq) {
          unsubscribe(); // torn down (or re-attached) while connecting
          return;
        }
        this._unsubscribeSocket = unsubscribe;
      })
      .catch((err) => {
        if (attachSeq !== this._attachSeq) return;
        console.warn("[VideoExportTabV2] socket unavailable, polling instead:", err?.message);
        this._startSnapshotPoll(jobId);
      });
  }

  _startSnapshotPoll(jobId) {
    this._stopSnapshotPoll();
    this._snapshotPoll = setInterval(async () => {
      try {
        const res = await authedFetch(`${apiBase()}/api/video-export-v2/${jobId}`);
        if (!res.ok) return;
        this._applySnapshot(await res.json());
      } catch {
        /* transient — next tick retries */
      }
    }, SNAPSHOT_POLL_MS);
  }

  _stopSnapshotPoll() {
    if (this._snapshotPoll) {
      clearInterval(this._snapshotPoll);
      this._snapshotPoll = null;
    }
  }

  /**
   * Replays a polled snapshot through _applyEvent as the events the socket
   * would have delivered, so both paths share one state machine. Only
   * changes are replayed: the snapshot's media URLs carry a fresh `_t=`
   * cache-buster on every GET, and re-applying them would reload the
   * preview video every tick.
   */
  _applySnapshot(snap) {
    if (!snap || snap.jobId !== this.state.jobId) return;
    const current = new Map(this.state.scenes.map((s) => [s.navItemId, s]));
    for (const s of snap.scenes || []) {
      const prev = current.get(s.navItemId);
      if (!prev) continue;
      const sameVideo = stripCacheBuster(prev.videoUrl) === stripCacheBuster(s.videoUrl);
      const sameThumb = stripCacheBuster(prev.thumbnailUrl) === stripCacheBuster(s.thumbnailUrl);
      if (
        prev.status === s.status &&
        prev.progress === s.progress &&
        prev.errorMessage === s.errorMessage &&
        sameVideo &&
        sameThumb
      ) {
        continue;
      }
      this._applyEvent({
        kind: "scene:lifecycle",
        jobId: snap.jobId,
        navItemId: s.navItemId,
        status: s.status,
        progress: s.progress,
        videoUrl: sameVideo ? prev.videoUrl : s.videoUrl,
        thumbnailUrl: sameThumb ? prev.thumbnailUrl : s.thumbnailUrl,
        durationSec: s.durationSec,
        errorMessage: s.errorMessage,
      });
    }

    const { phase, mergedVideoUrl } = this.state;
    if (
      snap.mergedVideoUrl &&
      stripCacheBuster(snap.mergedVideoUrl) !== stripCacheBuster(mergedVideoUrl)
    ) {
      this._applyEvent({
        kind: "merge:lifecycle",
        status: "completed",
        videoUrl: snap.mergedVideoUrl,
      });
    } else if (snap.status === "merging" && phase !== PHASE.MERGING) {
      this._applyEvent({ kind: "merge:lifecycle", status: "merging" });
    } else if (snap.status === "failed" && phase !== PHASE.FAILED) {
      this._applyEvent({ kind: "job:lifecycle", status: "failed" });
    } else if (
      snap.status === "cancelled" &&
      (phase === PHASE.RUNNING || phase === PHASE.MERGING)
    ) {
      this._applyEvent({ kind: "job:lifecycle", status: "cancelled" });
    }
  }

  _applyEvent(event) {
    if (event.kind === "scene:lifecycle") {
      this.setState((prev) => {
        const scenes = prev.scenes.map((s) =>
          s.navItemId === event.navItemId
            ? {
                ...s,
                status: event.status,
                progress: event.progress ?? s.progress,
                videoUrl: event.videoUrl ?? s.videoUrl,
                thumbnailUrl: event.thumbnailUrl ?? s.thumbnailUrl,
                durationSec: event.durationSec ?? s.durationSec,
                errorMessage: event.errorMessage,
              }
            : s
        );
        // Auto-promote first-completed to the selected tile so the big
        // preview has content as soon as something's playable.
        let selectedNavItemId = prev.selectedNavItemId;
        if (!selectedNavItemId && event.status === "completed" && event.videoUrl) {
          selectedNavItemId = event.navItemId;
        }
        return { scenes, selectedNavItemId };
      });
      this._maybeAdvancePhase();
    } else if (event.kind === "merge:lifecycle") {
      this.setState((prev) => ({
        mergeStatus: {
          status: event.status,
          progress: event.progress,
          errorMessage: event.errorMessage,
        },
        mergedVideoUrl: event.videoUrl ?? prev.mergedVideoUrl,
        // When merge completes, swap the big preview to the merged result.
        selectedNavItemId: event.status === "completed" ? "__MERGED__" : prev.selectedNavItemId,
        phase:
          event.status === "completed"
            ? PHASE.DONE
            : event.status === "merging"
              ? PHASE.MERGING
              : event.status === "failed"
                ? PHASE.FAILED
                : prev.phase,
        errorMessage: event.errorMessage || "",
      }));
      if (event.status === "completed") {
        // A fresh merge becomes the new "Orijinal" entry in the video select,
        // and it is the row a dub started from here would reference.
        this._fetchVideoVariants();
      }
    } else if (event.kind === "job:lifecycle") {
      if (event.status === "failed") {
        this.setState({
          phase: PHASE.FAILED,
          errorMessage: event.message || "Job failed",
        });
      } else if (event.status === "cancelled") {
        this.setState({
          phase: PHASE.IDLE,
          errorMessage: "Cancelled",
        });
      }
    }
  }

  _maybeAdvancePhase() {
    const { scenes, phase } = this.state;
    if (phase !== PHASE.RUNNING) return;
    const allDone = scenes.length > 0 && scenes.every((s) => s.status === "completed");
    if (allDone) this.setState({ phase: PHASE.AWAITING_MERGE });
  }

  async _stopJob() {
    if (!this.state.jobId) return;
    await authedFetch(`${apiBase()}/api/video-export-v2/${this.state.jobId}/cancel`, {
      method: "POST",
    });
  }

  /**
   * "Durdur" toolbar button handler — confirms first since this cancels
   * whatever is in flight (render or merge). NOT used by
   * `_toggleSkipMerge`, which already shows its own more specific
   * confirmation before calling `_stopJob()` directly — prompting twice
   * there would be redundant.
   */
  _onStopClick = () => {
    if (
      window.confirm(
        i18n.t(
          "video_export.stop_confirm",
          "Devam eden render/birleştirme işlemi iptal edilecek. Tamamlanmış sahne videoları silinmeyecek. Devam edilsin mi?"
        )
      )
    ) {
      this._stopJob();
    }
  };

  /**
   * Permanently delete every record (job, scenes, merge history) for this
   * training and return to the IDLE start screen — the "Temizle" button,
   * ported from console's VideoExportV2Modal. Confirms first since this
   * can't be undone; the socket is torn down before the request so no
   * stale event can repopulate state while the delete is in flight.
   */
  async _clearJob() {
    const tid = this._trainingId();
    if (!tid) return;
    if (
      !window.confirm(
        i18n.t(
          "video_export.clear_confirm",
          "Bu export'a ait tüm kayıtlar kalıcı olarak silinecek. Devam edilsin mi?"
        )
      )
    ) {
      return;
    }
    this.setState({ clearing: true });
    this._teardown();
    try {
      await authedFetch(`${apiBase()}/api/video-export-v2/by-training/${tid}`, {
        method: "DELETE",
      });
    } catch (err) {
      console.warn("[VideoExportTabV2] clear failed:", err);
    } finally {
      this.setState({
        clearing: false,
        jobId: null,
        // Back to IDLE should look exactly like the very first modal open —
        // all of the training's scenes waiting on the left, not an empty
        // strip — so re-populate from Redux instead of leaving `scenes: []`.
        scenes: this._loadLocalScenesFromStore(),
        mergedVideoUrl: null,
        mergeStatus: null,
        selectedNavItemId: null,
        order: [],
        skipMerge: new Set(),
        mergeHistory: null,
        mergeHistoryOpen: false,
        // Every export row for this training was just deleted, so the video
        // select (original + dubs) is empty too.
        videoVariants: [],
        selectedVariantId: null,
        phase: PHASE.IDLE,
      });
    }
  }

  /**
   * Pull the training's current scene list and merge any new scenes
   * into the popup's snapshot. Used after the user adds a new scene
   * in the editor while this popup is open — without this, the
   * snapshot is stuck at start() time and never shows the new scene.
   *
   * The backend is strictly additive: rendered videos, the merged
   * video, and order of existing scenes are all preserved.
   */
  async _reloadScenes() {
    if (!this.state.jobId) return;
    this.setState({ reloadingScenes: true });
    try {
      const res = await authedFetch(
        `${apiBase()}/api/video-export-v2/${this.state.jobId}/reload-scenes`,
        { method: "POST" }
      );
      if (!res.ok) throw new Error(`reload-scenes → ${res.status}`);
      const snap = await res.json();
      // Merge into local state, but DO NOT drop locally-known fields
      // like `progress` that the backend snapshot doesn't carry.
      this.setState((prev) => {
        const incoming = Array.isArray(snap?.scenes) ? snap.scenes : [];
        // Safety net: if the backend returned no scenes but we already
        // have some locally, keep the local list. A malformed or
        // partial response shouldn't wipe a working strip.
        if (incoming.length === 0 && prev.scenes.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(
            "[VideoExportTabV2] reload-scenes returned empty scenes — keeping local list"
          );
          return null;
        }
        const byId = new Map(prev.scenes.map((s) => [s.navItemId, s]));
        const nextScenes = incoming.map((s) => {
          const local = byId.get(s.navItemId);
          // For untouched existing scenes the backend may not include
          // ephemeral fields the live socket has been patching in
          // (progress, errorMessage). Keep those.
          if (local) {
            return {
              ...local,
              ...s,
              progress: s.progress ?? local.progress,
              errorMessage: s.errorMessage ?? local.errorMessage,
            };
          }
          return s;
        });
        return {
          scenes: nextScenes,
          // mergedVideoUrl is not in the reload-scenes response; preserve.
        };
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[VideoExportTabV2] reload-scenes failed:", err);
    } finally {
      this.setState({ reloadingScenes: false });
    }
  }

  /**
   * "Sahneleri Güncelle" toolbar button — replaces the old separate
   * "Sahne Sayısını Güncelle" (pure sync) / "Tümünü Yeniden Üret"
   * (nuke-everything) pair, which users found confusing side by side.
   * This does both safely in one step: sync the scene list (via
   * `_reloadScenes`, additive — never touches completed work), then
   * queue rendering ONLY for scenes that don't have a completed video
   * yet (new/queued/failed/cancelled). Scenes already completed or
   * actively rendering/uploading are left untouched. Forcing a redo of
   * an already-completed scene still works via that scene's own
   * per-tile regenerate icon.
   */
  async _syncAndFillScenes() {
    if (!this.state.jobId || this.state.reloadingScenes) return;
    await this._reloadScenes();
    // Scenes still marked "Don't Merge" are deliberately excluded from
    // the training's export (see _startExport) — leave them out here too
    // so a sync doesn't silently start rendering something the user
    // chose to sit out. Un-skip a scene first to have it picked up.
    const toFill = this.state.scenes
      .filter((s) => !this.state.skipMerge.has(s.navItemId))
      .filter((s) => s.status === "queued" || s.status === "failed" || s.status === "cancelled")
      .map((s) => s.navItemId);
    if (!toFill.length) return;
    const { selectedSubtitleLang } = this.state;
    this.setState({ reloadingScenes: true });
    try {
      await authedFetch(`${apiBase()}/api/video-export-v2/${this.state.jobId}/regenerate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          navItemIds: toFill,
          language: i18n.language,
          ...(selectedSubtitleLang ? { subtitleLanguage: selectedSubtitleLang } : {}),
        }),
      });
      this.setState((prev) => ({
        phase: PHASE.RUNNING,
        scenes: prev.scenes.map((s) =>
          toFill.includes(s.navItemId)
            ? { ...s, status: "queued", progress: 0, errorMessage: undefined }
            : s
        ),
      }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[VideoExportTabV2] sync-and-fill regenerate failed:", err);
    } finally {
      this.setState({ reloadingScenes: false });
    }
  }

  _onSyncScenesClick = () => {
    if (
      window.confirm(
        i18n.t(
          "video_export.sync_scenes_confirm",
          "Eğitimdeki güncel sahne listesi çekilecek; henüz render edilmemiş veya başarısız olan sahneler kuyruğa alınacak. Tamamlanmış sahne videolarına dokunulmayacak. Devam edilsin mi?"
        )
      )
    ) {
      this._syncAndFillScenes();
    }
  };

  /**
   * Probe Azure Blob storage for scene videos that were rendered before
   * but never tracked in the `idealStudioFiles` table (or whose rows
   * were lost). Registers fresh rows for whatever the probe finds, then
   * triggers a reload so the resurrection priority chain picks them up.
   *
   * Use case: the user sees a sea of `failed` / `queued` tiles even
   * though they're sure they rendered videos earlier. If the underlying
   * blobs survived in storage, this brings them back without forcing a
   * re-render. If nothing is found, the strip is unchanged and a console
   * line explains why.
   */
  async _recoverOldVideos() {
    if (!this.state.jobId) return;
    this.setState({ recoveringVideos: true });
    try {
      const res = await authedFetch(
        `${apiBase()}/api/video-export-v2/${this.state.jobId}/recover-scene-blobs`,
        { method: "POST" }
      );
      if (!res.ok) throw new Error(`recover-scene-blobs → ${res.status}`);
      const result = await res.json();
      // eslint-disable-next-line no-console
      console.log(
        `[VideoExportTabV2] recover-scene-blobs:`,
        `probed=${result.probed} recovered=${result.recovered} skipped=${result.skipped}`
      );
      // Always re-pull the scene list so any newly-registered rows
      // surface as completed tiles via the resurrection priority chain.
      await this._reloadScenes();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[VideoExportTabV2] recover-scene-blobs failed:", err);
    } finally {
      this.setState({ recoveringVideos: false });
    }
  }

  async _regenerateScene(navItemId) {
    if (!this.state.jobId) return;
    const { selectedSubtitleLang } = this.state;
    await authedFetch(`${apiBase()}/api/video-export-v2/${this.state.jobId}/regenerate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        navItemIds: [navItemId],
        language: i18n.language,
        ...(selectedSubtitleLang ? { subtitleLanguage: selectedSubtitleLang } : {}),
      }),
    });
    this.setState((prev) => ({
      phase: PHASE.RUNNING,
      scenes: prev.scenes.map((s) =>
        s.navItemId === navItemId
          ? { ...s, status: "queued", progress: 0, errorMessage: undefined }
          : s
      ),
    }));
  }

  async _cancelScene(navItemId) {
    if (!this.state.jobId) return;
    await authedFetch(`${apiBase()}/api/video-export-v2/${this.state.jobId}/cancel/${navItemId}`, {
      method: "POST",
    });
  }

  /**
   * Return scenes in the user's preferred order: tiles that the user
   * dragged into a new position win, anything else falls back to the
   * server's order. Run on every render so live events that add a new
   * scene get appended at the end (instead of disappearing because they
   * aren't in `order`).
   */
  _orderedScenes() {
    const { scenes, order } = this.state;
    if (!order.length) return scenes;
    const byId = new Map(scenes.map((s) => [s.navItemId, s]));
    const out = [];
    for (const id of order) {
      const s = byId.get(id);
      if (s) {
        out.push(s);
        byId.delete(id);
      }
    }
    // Any new scenes that arrived after the last reorder go to the end.
    for (const s of byId.values()) out.push(s);
    return out;
  }

  async _mergeAll() {
    if (!this.state.jobId) return;
    const ordered = this._orderedScenes();
    const navItemIds = ordered
      .filter((s) => s.status === "completed" && !this.state.skipMerge.has(s.navItemId))
      .map((s) => s.navItemId);
    if (!navItemIds.length) return;
    await authedFetch(`${apiBase()}/api/video-export-v2/${this.state.jobId}/merge`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ navItemIds }),
    });
    // Reset MERGE state for a fresh run. Clear the previous merged video so
    // the strip's merged slot and the big preview both fall back to their
    // "merging…" loading state instead of showing the stale result while
    // the new merge is in flight — otherwise a re-merge looks like a no-op
    // until the new video suddenly swaps in.
    this.setState({
      phase: PHASE.MERGING,
      mergeStatus: { status: "queued", progress: 0 },
      mergedVideoUrl: null,
    });
  }

  /**
   * Lazy-load the merge-history list (newest first) from
   * /by-training/:tid/merges and toggle the dropdown. Re-fetched on
   * every open so a fresh merge that just landed shows up without a
   * page reload.
   */
  async _toggleMergeHistory() {
    if (this.state.mergeHistoryOpen) {
      this.setState({ mergeHistoryOpen: false });
      return;
    }
    const tid = this._trainingId();
    if (!tid) return;
    this.setState({ mergeHistoryOpen: true, mergeHistoryLoading: true });
    try {
      const res = await authedFetch(`${apiBase()}/api/video-export-v2/by-training/${tid}/merges`);
      const list = res.ok ? await res.json() : [];
      this.setState({ mergeHistory: Array.isArray(list) ? list : [], mergeHistoryLoading: false });
    } catch (err) {
      console.warn("[VideoExportTabV2] mergeHistory fetch failed:", err);
      this.setState({ mergeHistory: [], mergeHistoryLoading: false });
    }
  }

  /**
   * Select a historic merged-video URL into the big preview. Closes
   * the dropdown but doesn't kick off a new merge — that's a separate
   * button press.
   */
  _selectHistoricMerge(item) {
    this.setState({
      mergeHistoryOpen: false,
      // Reuse the merged-preview path: setting mergedVideoUrl + the
      // __MERGED__ selectedNavItemId is what the preview reads.
      mergedVideoUrl: item.url,
      selectedNavItemId: "__MERGED__",
      // Keep the variant select in sync. A historic merge that is no longer
      // the newest is absent from the (collapsed) variant list, so this
      // clears the select rather than leaving it pointing at the wrong row.
      selectedVariantId: this.state.videoVariants.find((v) => v.id === item.id)?.id ?? null,
    });
  }

  // ── Video translation (dubbing) ────────────────────────────────────────

  /**
   * Kick off a dub into `language`. The whole chain (transcribe → translate →
   * TTS → mux) is ONE backend job, so all we do here is POST and then poll
   * that jobId — there is no per-stage client orchestration.
   *
   * `sourceVideoUrl` is whatever the user is currently watching, so dubbing
   * the entry they picked from the history dropdown works as expected. The
   * backend falls back to the training's videoUrl when it is omitted.
   */
  async _startTranslation(language) {
    const tid = this._trainingId();
    if (!tid || this.state.translateJob) return;

    const variant = this.state.videoVariants.find((v) => v.id === this.state.selectedVariantId);
    // Only ever dub an ORIGINAL as the source — dubbing a dub would stack
    // machine translation on machine translation.
    const source =
      variant && variant.isOriginal ? variant : this.state.videoVariants.find((v) => v.isOriginal);

    this.setState({
      // The modal stays OPEN so the user watches the progress bar they just
      // started, and can queue the next language when it finishes.
      translateError: null,
      translateJob: {
        jobId: null,
        language,
        progress: 0,
        message: i18n.t("video_export.translate_starting", "Başlatılıyor…"),
      },
    });

    try {
      const res = await authedFetch(`${apiBase()}/api/video-translation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idealStudioTrainingId: tid,
          targetLanguage: language,
          ...(source?.url ? { sourceVideoUrl: source.url } : {}),
          ...(source?.id ? { sourceFileId: source.id } : {}),
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(
          data?.message || i18n.t("video_export.translate_failed", "Çeviri başlatılamadı.")
        );
      }
      this.setState((prev) => ({
        translateJob: { ...prev.translateJob, jobId: data.jobId },
      }));
      this._pollTranslation(data.jobId);
    } catch (err) {
      this.setState({
        translateJob: null,
        translateError: err?.message || String(err),
      });
    }
  }

  /**
   * Poll the dub job until it settles.
   *
   * Polling rather than the export socket on purpose: this job runs on the
   * generic media-job pipeline (jobType 'video-translation'), not on the
   * video-export event bus this modal's socket is wired to. A 5s tick on a
   * job that runs for minutes is cheap, and it keeps the two progress
   * channels from fighting over `phase`.
   */
  _pollTranslation(jobId) {
    this._clearTranslationPoll();
    const tick = async () => {
      try {
        const res = await authedFetch(`${apiBase()}/api/video-translation/jobs/${jobId}`);
        if (!res.ok) return; // transient — try again on the next tick
        const job = await res.json();
        const progress = job?.progress?.progress ?? job?.progress ?? 0;
        const message = job?.progress?.message ?? "";

        if (job?.status === "completed") {
          this._clearTranslationPoll();
          // Dublaj bu sekmede tüketiliyor (varyant listesi + önizleme) → widget'taki
          // video-translation kartı "Onay Bekliyor"da kalmasın.
          approveJob(jobId);
          this.setState({ translateJob: null, translateError: null });
          await this._fetchVideoVariants();
          // Drop the finished dub straight into the preview — that is what
          // the user pressed the button to see.
          if (job?.result?.videoUrl) {
            this.setState({
              mergedVideoUrl: job.result.videoUrl,
              selectedNavItemId: "__MERGED__",
              selectedVariantId: job.result.fileId ?? null,
            });
          }
          return;
        }

        if (job?.status === "failed") {
          this._clearTranslationPoll();
          this.setState({
            translateJob: null,
            translateError:
              job?.error || i18n.t("video_export.translate_failed", "Çeviri başarısız oldu."),
          });
          return;
        }

        this.setState((prev) =>
          prev.translateJob ? { translateJob: { ...prev.translateJob, progress, message } } : null
        );
      } catch {
        /* transient network error — the next tick retries */
      }
    };
    tick();
    this._translationPoll = setInterval(tick, 5000);
  }

  _clearTranslationPoll() {
    if (this._translationPoll) {
      clearInterval(this._translationPoll);
      this._translationPoll = null;
    }
  }

  /**
   * Pick a stored video (original merge or a dub) into the big preview.
   * Reuses the merged-preview path — `mergedVideoUrl` + the `__MERGED__`
   * selection is what the player reads.
   */
  _selectVariant = (fileId) => {
    const variant = this.state.videoVariants.find((v) => v.id === fileId);
    if (!variant) return;
    this.setState({
      selectedVariantId: variant.id,
      mergedVideoUrl: variant.url,
      selectedNavItemId: "__MERGED__",
    });
  };

  _selectTile(navItemId) {
    this.setState({ selectedNavItemId: navItemId });
  }

  /**
   * Toggling a scene OUT of the merge while a merge is already running is
   * confusing on its own — the in-flight merge already committed to
   * including this scene, so the toggle would silently do nothing until
   * the NEXT merge attempt. Ask first; if the user confirms, cancel the
   * running job (the socket's job:lifecycle "cancelled" event settles
   * `phase` back to IDLE shortly after) so "Merge"/"Re-merge" is pressed
   * fresh with the new selection. Re-including a scene (toggling back on)
   * never needs this — it's harmless either way.
   */
  async _toggleSkipMerge(navItemId) {
    const isCurrentlySkipped = this.state.skipMerge.has(navItemId);
    if (!isCurrentlySkipped && this.state.phase === PHASE.MERGING) {
      const confirmed = window.confirm(
        i18n.t(
          "video_export.merge_cancel_confirm",
          "Bir birleştirme işlemi zaten devam ediyor. Bu sahneyi hariç tutmak için mevcut birleştirmeyi iptal etmemiz gerekiyor — video yeniden birleştirilmesi gerekecek. Devam edilsin mi?"
        )
      );
      if (!confirmed) return;
      await this._stopJob();
    }
    this.setState((prev) => {
      const next = new Set(prev.skipMerge);
      if (next.has(navItemId)) next.delete(navItemId);
      else next.add(navItemId);
      return { skipMerge: next };
    });
  }

  _onDragStart(navItemId) {
    this.setState({ draggingNavItemId: navItemId });
  }

  _onDragEnd() {
    this.setState({ draggingNavItemId: null });
  }

  /**
   * Move the dragged tile to the position of `targetNavItemId`. Idempotent
   * when the drop lands on the dragged tile itself. Order is stored as
   * an explicit array so it survives subsequent live updates from the
   * socket — see `_orderedScenes()`.
   */
  _onDropOnTile(targetNavItemId) {
    const draggingId = this.state.draggingNavItemId;
    this.setState({ draggingNavItemId: null });
    if (!draggingId || draggingId === targetNavItemId) return;
    const ordered = this._orderedScenes().map((s) => s.navItemId);
    const from = ordered.indexOf(draggingId);
    const to = ordered.indexOf(targetNavItemId);
    if (from < 0 || to < 0) return;
    ordered.splice(to, 0, ordered.splice(from, 1)[0]);
    this.setState({ order: ordered });
  }

  _renderPreview() {
    const { selectedNavItemId, mergedVideoUrl, scenes, phase } = this.state;
    if (selectedNavItemId === "__MERGED__" && mergedVideoUrl) {
      return (
        <div className="vexp-preview-stage" tabIndex={0} onKeyDown={this._onPreviewKeyDown}>
          <div className="vexp-preview-label">
            {i18n.t("video_export.merged_label", "Birleştirilmiş video")}
          </div>
          {/* No autoPlay — popup opening / merge auto-select must NOT
              start playback. preload="auto" + playsInline so the browser
              fetches enough of the moov atom to allow seeking; without
              "auto" some browsers wait until the user presses play and
              then refuse to seek until the metadata is in. */}
          <video
            ref={this._previewVideoRef}
            src={mergedVideoUrl}
            controls
            preload="auto"
            playsInline
            controlsList="nodownload"
            key={mergedVideoUrl}
          />
        </div>
      );
    }
    if (selectedNavItemId === "__MERGED__" && !mergedVideoUrl) {
      // A (re-)merge is in flight and any previous merged video has been
      // cleared — show a loading state instead of falling through to the
      // "no scene selected" branch below, which would otherwise render a
      // confusing message while the merge is actually running.
      return (
        <div className="vexp-preview-empty vexp-preview-merging">
          <HiArrowPath className="spin" />
          <div>{i18n.t("video_export.merging_placeholder", "Birleştiriliyor…")}</div>
        </div>
      );
    }
    const scene = scenes.find((s) => s.navItemId === selectedNavItemId);
    const noCompletedYet = scenes.length > 0 && !scenes.some((s) => s.status === "completed");
    // Skeleton: render is running but no scene has finished — show a
    // shimmering placeholder where the video will go, rather than a
    // bare "no scene selected" message.
    if (phase === PHASE.RUNNING && noCompletedYet) {
      return <PreviewSkeleton scenes={scenes} />;
    }
    if (!scene) {
      return (
        <div className="vexp-preview-empty">
          {phase === PHASE.IDLE
            ? i18n.t(
                "video_export.idle_hint",
                "Sahneleri üretmeye başlamak için yukarıdaki butona tıklayın."
              )
            : phase === PHASE.STARTING
              ? i18n.t("video_export.starting", "İş başlatılıyor…")
              : phase === PHASE.CHECKING
                ? i18n.t("video_export.checking", "Mevcut iş kontrol ediliyor…")
                : i18n.t("video_export.select_scene", "Bir sahne seçin")}
        </div>
      );
    }
    if (!scene.videoUrl) {
      const pct = Math.round((scene.progress || 0) * 100);
      return (
        <div className="vexp-preview-stage vexp-preview-pending">
          <div className="vexp-preview-label">
            {scene.navItemId} —{" "}
            {scene.status === "rendering"
              ? i18n.t("video_export.render_percent", { pct }) || `Render %${pct}`
              : translateStatus(scene.status)}
          </div>
          {scene.thumbnailUrl ? (
            <img src={scene.thumbnailUrl} alt="" />
          ) : (
            <div className="vexp-preview-placeholder">
              <HiVideoCamera />
            </div>
          )}
          {scene.errorMessage && <div className="vexp-preview-error">{scene.errorMessage}</div>}
        </div>
      );
    }
    const previewDur = fmtDuration(scene.durationSec);
    return (
      <div className="vexp-preview-stage" tabIndex={0} onKeyDown={this._onPreviewKeyDown}>
        <div className="vexp-preview-label">
          {scene.navItemId}
          {previewDur && <span className="vexp-preview-duration"> · {previewDur}</span>}
        </div>
        <video
          ref={this._previewVideoRef}
          src={scene.videoUrl}
          controls
          preload="auto"
          playsInline
          controlsList="nodownload"
          poster={scene.thumbnailUrl || undefined}
          key={scene.videoUrl}
        />
      </div>
    );
  }

  render() {
    const {
      phase,
      jobId,
      errorMessage,
      selectedNavItemId,
      mergedVideoUrl,
      skipMerge,
      draggingNavItemId,
      subtitleLanguages,
      selectedSubtitleLang,
      videoVariants,
      selectedVariantId,
      translateLanguages,
      translateJob,
      translateError,
    } = this.state;
    const scenes = this._orderedScenes();
    // Sahne adları: canlı store önce, API'nin snapshot'taki `title`ı yedek
    // (bkz. readLocalSceneTitles). Render başına bir kez kuruluyor.
    const localSceneTitles = readLocalSceneTitles();
    const mergeable = scenes.filter((s) => !skipMerge.has(s.navItemId));
    const allDone = mergeable.length > 0 && mergeable.every((s) => s.status === "completed");
    // Total + ready include ONLY scenes the user hasn't excluded from
    // the merge — so the chip reflects what the final mp4 will look like.
    const totalSec = mergeable.reduce((acc, s) => acc + (Number(s.durationSec) || 0), 0);
    const totalLabel = totalSec > 0 ? fmtDuration(totalSec) : null;
    const readySec = mergeable
      .filter((s) => s.status === "completed")
      .reduce((acc, s) => acc + (Number(s.durationSec) || 0), 0);
    const readyLabel = readySec > 0 ? fmtDuration(readySec) : null;
    // Badge on the Diller button: how many dubbed versions already exist, so
    // the user can see there is something in there without opening it.
    const dubCount = videoVariants.filter((v) => !v.isOriginal).length;

    return (
      <div className="video-export-v2-tab">
        {phase === PHASE.CHECKING && (
          <div className="vexp-checking">
            <HiArrowPath className="spin" />
            <span>{i18n.t("video_export.checking", "Mevcut iş kontrol ediliyor…")}</span>
          </div>
        )}

        {phase !== PHASE.FAILED && phase !== PHASE.CHECKING && (
          <>
            <div className="vexp-toolbar">
              {phase === PHASE.IDLE ? (
                <>
                  <div className="vexp-toolbar-spacer" />
                  <button type="button" className="vexp-btn primary" onClick={this._openStartModal}>
                    <HiPlay /> {i18n.t("video_export.start", "Başla")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="vexp-btn"
                    onClick={this._onSyncScenesClick}
                    disabled={!!this.state.reloadingScenes}
                    title={i18n.t(
                      "video_export.sync_scenes_tooltip",
                      "Eğitimin güncel sahne listesini çek ve render edilmemiş veya başarısız sahneleri kuyruğa al. Tamamlanmış videolara dokunulmaz."
                    )}
                  >
                    <HiArrowPath className={this.state.reloadingScenes ? "spin" : ""} />{" "}
                    {i18n.t("video_export.sync_scenes", "Sahneleri Güncelle")}
                  </button>
                  {/* "Recover Old Videos" button intentionally hidden per
                      user request. The _recoverOldVideos() handler and the
                      backend POST /:jobId/recover-scene-blobs endpoint stay
                      intact so this can be re-enabled by flipping the
                      surrounding `false &&` to a flag without touching the
                      underlying flow. */}
                  {false && (
                    <button
                      type="button"
                      className="vexp-btn"
                      onClick={() => this._recoverOldVideos()}
                      disabled={!!this.state.recoveringVideos || !!this.state.reloadingScenes}
                      title={i18n.t(
                        "video_export.recover_tooltip",
                        "Veritabanında kaydı olmayan, daha önce render edilmiş sahne videolarını Azure Blob'da ara."
                      )}
                    >
                      {this.state.recoveringVideos ? (
                        <>
                          <HiArrowPath className="spin" />{" "}
                          {i18n.t("video_export.recovering", "Aranıyor…")}
                        </>
                      ) : (
                        <>
                          <HiMagnifyingGlass />{" "}
                          {i18n.t("video_export.recover", "Eski Videoları Kurtar")}
                        </>
                      )}
                    </button>
                  )}
                  {/* Only shown while something is actually in flight — no
                      point offering to stop a job that isn't running. */}
                  {(phase === PHASE.RUNNING ||
                    phase === PHASE.MERGING ||
                    phase === PHASE.STARTING) && (
                    <button
                      type="button"
                      className="vexp-btn danger"
                      onClick={this._onStopClick}
                      disabled={this.state.clearing}
                    >
                      <HiStop /> {i18n.t("video_export.stop", "Tümünü Durdur")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="vexp-btn"
                    onClick={() => this._clearJob()}
                    disabled={!!this.state.clearing}
                    title={i18n.t(
                      "video_export.clear_tooltip",
                      "Bu training için tüm export kayıtlarını siler ve baştan başlar"
                    )}
                  >
                    {this.state.clearing ? (
                      <>
                        <HiArrowPath className="spin" />{" "}
                        {i18n.t("video_export.clearing", "Yeniden başlatılıyor…")}
                      </>
                    ) : (
                      <>
                        <HiArrowUturnLeft /> {i18n.t("video_export.clear", "Yeniden Başla")}
                      </>
                    )}
                  </button>
                  <div className="vexp-toolbar-spacer" />
                </>
              )}
              {phase !== PHASE.IDLE && totalLabel && (
                <div
                  className="vexp-duration-summary"
                  title={i18n.t("video_export.duration_tooltip", "Toplam süre")}
                >
                  <HiClock />
                  <span>{readyLabel ? `${readyLabel} / ${totalLabel}` : totalLabel}</span>
                </div>
              )}
              {phase !== PHASE.IDLE && (
                <>
                  {/* Everything language-related — which dub to watch, and
                      producing a new one — lives behind this ONE button.
                      Inline controls for it used to sit in this toolbar and
                      made the default screen hard to read; the modal keeps
                      the toolbar to the actions that matter every time. */}
                  {translateLanguages.length > 0 && (
                    <button
                      type="button"
                      className="vexp-btn"
                      onClick={() => this.setState({ languageModalOpen: true })}
                      title={i18n.t(
                        "video_export.languages_tooltip",
                        "Videoyu başka dillerde üret ve izle"
                      )}
                    >
                      {translateJob ? (
                        <>
                          <HiArrowPath className="spin" /> {langLabel(translateJob.language)}{" "}
                          {translateJob.progress ? `%${translateJob.progress}` : ""}
                        </>
                      ) : (
                        <>
                          <HiLanguage /> {i18n.t("video_export.languages", "Diller")}
                          {dubCount > 0 && <span className="vexp-btn-count">{dubCount}</span>}
                        </>
                      )}
                    </button>
                  )}

                  {/* History dropdown — every prior merged-video for this
                      training is listed, newest first. Click an entry to
                      pull it into the big preview without kicking off a new
                      merge. */}
                  <div className="vexp-history-wrap">
                    <button
                      type="button"
                      className="vexp-btn"
                      onClick={() => this._toggleMergeHistory()}
                      title={i18n.t(
                        "video_export.history_tooltip",
                        "Önceki birleştirilmiş videolar"
                      )}
                    >
                      <HiClock /> {i18n.t("video_export.history", "Geçmiş")}
                    </button>
                    {this.state.mergeHistoryOpen && (
                      <MergeHistoryDropdown
                        items={this.state.mergeHistory}
                        loading={this.state.mergeHistoryLoading}
                        onPick={(item) => this._selectHistoricMerge(item)}
                        onClose={() => this.setState({ mergeHistoryOpen: false })}
                        downloadingKey={this.state.downloadingKey}
                        onDownload={this._downloadWithLoading}
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    className="vexp-btn primary"
                    onClick={() => this._mergeAll()}
                    disabled={!allDone || phase === PHASE.MERGING || this.state.clearing}
                    title={
                      allDone
                        ? mergedVideoUrl
                          ? i18n.t(
                              "video_export.merge_reselect_tooltip",
                              "Yeni seçim ile yeniden birleştir"
                            )
                          : ""
                        : i18n.t(
                            "video_export.merge_disabled_tooltip",
                            "Tüm sahneler hazır olduğunda etkinleşir"
                          )
                    }
                  >
                    <HiPlay />{" "}
                    {mergedVideoUrl
                      ? i18n.t("video_export.re_merge", "Yeniden Birleştir")
                      : i18n.t("video_export.merge", "Birleştir")}
                  </button>
                </>
              )}
            </div>

            {/* Left column: scene strip (top-to-bottom, matching the rest
                of the app's scene ordering convention) + the big preview
                area to its right. */}
            <div className="vexp-main-area">
              {/* Scene strip: scrollable scenes on top, a divider, then a
                pinned "merged result" slot at the bottom that is ALWAYS
                rendered (placeholder → spinner → merged tile) so the
                combined video always has its own dedicated, always-visible
                spot instead of appearing only once a merge has actually
                run. Hidden entirely before the first export ever starts
                (nothing to show yet). */}
              {scenes.length > 0 && (
                <div className="vexp-strip">
                  <div className="vexp-strip-scenes">
                    {scenes.map((s, i) => (
                      <SceneTile
                        key={s.navItemId}
                        index={i + 1}
                        scene={s}
                        title={localSceneTitles[s.navItemId] || s.title || ""}
                        selected={selectedNavItemId === s.navItemId}
                        isSkipped={skipMerge.has(s.navItemId)}
                        // Bir export işi açılmış mı? Açılmadıysa sahne
                        // kartında durum rozeti ve iptal/yeniden-üret
                        // düğmeleri gösterilmiyor.
                        started={!!jobId}
                        isDragging={draggingNavItemId === s.navItemId}
                        isDownloading={this.state.downloadingKey === `scene:${s.navItemId}`}
                        onSelect={() => this._selectTile(s.navItemId)}
                        onRegenerate={() => this._openRegenerateModal(s.navItemId)}
                        onCancel={() => this._cancelScene(s.navItemId)}
                        onToggleSkipMerge={() => this._toggleSkipMerge(s.navItemId)}
                        onDownload={() =>
                          this._downloadWithLoading(
                            `scene:${s.navItemId}`,
                            s.videoUrl,
                            `${s.navItemId}.mp4`
                          )
                        }
                        onDragStart={() => this._onDragStart(s.navItemId)}
                        onDragEnd={() => this._onDragEnd()}
                        onDropOnTile={() => this._onDropOnTile(s.navItemId)}
                      />
                    ))}
                  </div>
                  <div className="vexp-strip-divider" aria-hidden="true" />
                  <div className="vexp-merged-slot">
                    {mergedVideoUrl ? (
                      <MergedTile
                        selected={selectedNavItemId === "__MERGED__"}
                        onSelect={() => this._selectTile("__MERGED__")}
                        url={mergedVideoUrl}
                        isDownloading={this.state.downloadingKey === "merged"}
                        onDownload={() =>
                          this._downloadWithLoading(
                            "merged",
                            mergedVideoUrl,
                            `${sanitizeFileNameStem(this.props.trainingName)}.mp4`
                          )
                        }
                      />
                    ) : (
                      <div
                        className={
                          "vexp-tile vexp-tile-merged-placeholder" +
                          (phase === PHASE.MERGING ? " is-merging" : "")
                        }
                      >
                        <div className="vexp-tile-thumb">
                          {phase === PHASE.MERGING ? (
                            <>
                              <HiArrowPath className="spin" />
                              <span className="vexp-merged-placeholder-label">
                                {i18n.t("video_export.merging_placeholder", "Birleştiriliyor…")}
                              </span>
                            </>
                          ) : (
                            <HiPlay className="vexp-merged-placeholder-icon" />
                          )}
                        </div>
                        <div className="vexp-tile-meta">
                          <span className="vexp-status-pill">
                            {phase === PHASE.MERGING
                              ? i18n.t("video_export.merging_placeholder", "Birleştiriliyor…")
                              : i18n.t("video_export.status_merge_pending", "Birleştirme bekliyor")}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="vexp-preview-area">{this._renderPreview()}</div>
            </div>

            {phase === PHASE.MERGING && (
              <div className="vexp-merge-status">
                {i18n.t("video_export.merging_progress", {
                  pct: Math.round((this.state.mergeStatus?.progress ?? 0) * 100),
                }) ||
                  `Birleştiriliyor… ${Math.round((this.state.mergeStatus?.progress ?? 0) * 100)}%`}
              </div>
            )}
          </>
        )}

        {phase === PHASE.FAILED && (
          <div className="vexp-error">
            {i18n.t("video_export.error_prefix", "Hata")}: {errorMessage}
            <button type="button" className="vexp-btn" onClick={() => this._startExport(true)}>
              <HiArrowPath /> {i18n.t("video_export.retry", "Yeniden Dene")}
            </button>
          </div>
        )}

        {/* "Başla" confirm overlay — asks for an optional subtitle
            language before the very first export request goes out. */}
        {this.state.showStartModal && (
          <div className="vexp-start-overlay" onClick={this._closeStartModal}>
            <div className="vexp-start-card" onClick={(e) => e.stopPropagation()}>
              <h3>{i18n.t("video_export.start_modal_title", "Video Üretimini Başlat")}</h3>
              <p>
                {i18n.t(
                  "video_export.start_hint",
                  "Sahneleri tek tek üret, kontrol et, beğenmediklerini yeniden ürettir. Hazır olduğunda manuel birleştir butonuna basarak nihai videoyu oluştur."
                )}
              </p>
              <div className="vexp-save-warning">
                <HiExclamationTriangle />
                {i18n.t(
                  "video_export.save_reminder",
                  "Eğitimi kaydetmeyi unutmayın. Kaydedilmemiş değişiklikler video üretimine dahil edilmez."
                )}
              </div>
              {subtitleLanguages.length > 0 && (
                <div className="vexp-lang-select">
                  <label>
                    {i18n.t("video_export.subtitle_lang_label", "Altyazı dili (opsiyonel)")}
                  </label>
                  <select
                    value={selectedSubtitleLang}
                    onChange={(e) => this.setState({ selectedSubtitleLang: e.target.value })}
                  >
                    <option value="">
                      {i18n.t("video_export.subtitle_none", "— Altyazısız —")}
                    </option>
                    {subtitleLanguages.map((l) => (
                      <option key={l.languageCode} value={l.languageCode}>
                        {l.languageCode} (
                        {i18n.t("video_export.subtitle_scene_count", { count: l.subtitleCount }) ||
                          `${l.subtitleCount} sahne`}
                        )
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="vexp-start-actions">
                <button type="button" className="vexp-btn" onClick={this._closeStartModal}>
                  {i18n.t("video_export.cancel", "İptal")}
                </button>
                <button type="button" className="vexp-btn primary" onClick={this._confirmStart}>
                  <HiPlay /> {i18n.t("video_export.start", "Başla")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Per-scene "Yeniden Üret" confirm overlay — same language-picker
            pattern as "Başla", scoped to the one scene the user clicked. */}
        {this.state.regenerateModalNavItemId && (
          <div className="vexp-start-overlay" onClick={this._closeRegenerateModal}>
            <div className="vexp-start-card" onClick={(e) => e.stopPropagation()}>
              <h3>{i18n.t("video_export.regenerate_modal_title", "Sahneyi Yeniden Üret")}</h3>
              <p>
                {i18n.t(
                  "video_export.regenerate_modal_hint",
                  "Bu sahne yeniden oluşturulacak. Mevcut video, işlem tamamlanana kadar yerinde kalır."
                )}
              </p>
              <div className="vexp-save-warning">
                <HiExclamationTriangle />
                {i18n.t(
                  "video_export.save_reminder",
                  "Eğitimi kaydetmeyi unutmayın. Kaydedilmemiş değişiklikler video üretimine dahil edilmez."
                )}
              </div>
              {subtitleLanguages.length > 0 && (
                <div className="vexp-lang-select">
                  <label>
                    {i18n.t("video_export.subtitle_lang_label", "Altyazı dili (opsiyonel)")}
                  </label>
                  <select
                    value={selectedSubtitleLang}
                    onChange={(e) => this.setState({ selectedSubtitleLang: e.target.value })}
                  >
                    <option value="">
                      {i18n.t("video_export.subtitle_none", "— Altyazısız —")}
                    </option>
                    {subtitleLanguages.map((l) => (
                      <option key={l.languageCode} value={l.languageCode}>
                        {l.languageCode} (
                        {i18n.t("video_export.subtitle_scene_count", { count: l.subtitleCount }) ||
                          `${l.subtitleCount} sahne`}
                        )
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="vexp-start-actions">
                <button type="button" className="vexp-btn" onClick={this._closeRegenerateModal}>
                  {i18n.t("video_export.cancel", "İptal")}
                </button>
                <button
                  type="button"
                  className="vexp-btn primary"
                  onClick={this._confirmRegenerateScene}
                >
                  <HiArrowPath />{" "}
                  {i18n.t("video_export.regenerate_scene", "Bu Sahneyi Yeniden Üret")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Language studio — the whole dubbing feature behind one button. */}
        {this.state.languageModalOpen && (
          <LanguageModal
            variants={videoVariants}
            selectedVariantId={selectedVariantId}
            languages={translateLanguages}
            job={translateJob}
            error={translateError}
            downloadingKey={this.state.downloadingKey}
            onWatch={(id) => {
              this._selectVariant(id);
              this.setState({ languageModalOpen: false });
            }}
            onDownload={this._downloadWithLoading}
            onGenerate={(lang) => this._startTranslation(lang)}
            onDismissError={() => this.setState({ translateError: null })}
            onClose={() => this.setState({ languageModalOpen: false })}
          />
        )}
      </div>
    );
  }
}

/**
 * Everything about dubbed versions of the merged video, in one place: which
 * language to watch, and producing a new one.
 *
 * A modal rather than toolbar controls because the toolbar is read on every
 * export, while languages are touched occasionally — and a select, a language
 * dropdown and a progress strip sitting there permanently made the common
 * case harder to scan.
 */
/** Languages shown before "show more" — see the note in LanguageModal. */
const LANGUAGE_PREVIEW_COUNT = 3;

const LanguageModal = ({
  variants,
  selectedVariantId,
  languages,
  job,
  error,
  downloadingKey,
  onWatch,
  onDownload,
  onGenerate,
  onDismissError,
  onClose,
}) => {
  const list = Array.isArray(variants) ? variants : [];
  const original = list.find((v) => v.isOriginal);
  const dubs = list.filter((v) => !v.isOriginal);
  const done = new Set(dubs.map((v) => v.language));
  // Only an ORIGINAL can be dubbed — dubbing a dub would stack machine
  // translation on machine translation.
  const canGenerate = !!original && !job;

  // The catalog runs to a dozen languages; showing them all turns the modal
  // into a wall of chips before the user has even read the list of videos
  // above. Three covers the common case, the rest are one click away.
  const [showAllLanguages, setShowAllLanguages] = useState(false);
  const visibleLanguages = showAllLanguages
    ? languages
    : languages.slice(0, LANGUAGE_PREVIEW_COUNT);
  const hiddenCount = Math.max(0, languages.length - LANGUAGE_PREVIEW_COUNT);

  // Picking a language used to fire the job immediately. Dubbing costs minutes
  // of worker time and writes a new video, so a mis-click was expensive and
  // irreversible — confirm first.
  const [pendingLanguage, setPendingLanguage] = useState(null);
  // A dub that just started clears the pending state; leaving it up would show
  // a confirm panel for work already running.
  useEffect(() => {
    if (job) setPendingLanguage(null);
  }, [job]);

  const row = (variant, label) => {
    const isSelected = variant.id === selectedVariantId;
    const key = `variant:${variant.id}`;
    return (
      <div key={variant.id} className={`vexp-lang-row${isSelected ? " selected" : ""}`}>
        <div className="vexp-lang-row-main">
          <span className="vexp-lang-row-name">{label}</span>
          <span className="vexp-lang-row-meta">
            {variant.durationSec ? fmtDuration(variant.durationSec) : null}
            {variant.fileSize ? ` · ${(variant.fileSize / (1024 * 1024)).toFixed(1)} MB` : null}
          </span>
        </div>
        <div className="vexp-lang-row-actions">
          <button type="button" className="vexp-btn" onClick={() => onWatch(variant.id)}>
            <HiPlay /> {i18n.t("video_export.watch", "İzle")}
          </button>
          <button
            type="button"
            className="vexp-btn"
            disabled={downloadingKey === key}
            title={i18n.t("video_export.download", "İndir")}
            aria-label={i18n.t("video_export.download", "İndir")}
            onClick={() =>
              onDownload(key, variant.url, `video-${variant.language || "original"}.mp4`)
            }
          >
            {downloadingKey === key ? (
              <HiArrowPath className="spin" aria-hidden="true" />
            ) : (
              <HiArrowDownTray aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="vexp-start-overlay" onClick={onClose}>
      <div className="vexp-start-card vexp-lang-card" onClick={(e) => e.stopPropagation()}>
        <h3>
          <HiLanguage /> {i18n.t("video_export.languages_title", "Diller")}
        </h3>
        <p>
          {i18n.t(
            "video_export.languages_hint",
            "Birleştirilmiş videoyu başka bir dilde seslendirin. Sahneler değişmez, yalnızca ses değişir."
          )}
        </p>

        {/* Only this middle part scrolls, so the title and the Kapat button
            stay reachable however long the video / language lists get. */}
        <div className="vexp-lang-scroll">
          {error && (
            <div className="vexp-lang-error">
              <HiExclamationTriangle />
              <span>{error}</span>
              <button type="button" className="vexp-btn" onClick={onDismissError}>
                {i18n.t("video_export.dismiss", "Kapat")}
              </button>
            </div>
          )}

          {job && (
            <div className="vexp-lang-progress">
              <div className="vexp-lang-progress-head">
                <HiArrowPath className="spin" />
                <span>
                  {langLabel(job.language)}
                  {job.message ? ` — ${job.message}` : ""}
                </span>
                <strong>{job.progress ? `%${job.progress}` : ""}</strong>
              </div>
              <div className="vexp-lang-progress-track">
                <div
                  className="vexp-lang-progress-fill"
                  style={{ width: `${Math.max(2, Number(job.progress) || 0)}%` }}
                />
              </div>
            </div>
          )}

          <div className="vexp-lang-section">
            <div className="vexp-lang-section-title">
              {i18n.t("video_export.languages_available", "Hazır videolar")}
            </div>
            {!original && (
              <div className="vexp-lang-empty">
                {i18n.t(
                  "video_export.languages_needs_merge",
                  "Önce videoyu birleştirin — çeviri birleştirilmiş video üzerinden yapılır."
                )}
              </div>
            )}
            {original && row(original, i18n.t("video_export.variant_original", "Orijinal"))}
            {dubs.map((v) => row(v, langLabel(v.language)))}
          </div>

          <div className="vexp-lang-section">
            <div className="vexp-lang-section-title">
              {i18n.t("video_export.languages_add", "Yeni dil ekle")}
            </div>
            {languages.length === 0 && (
              <div className="vexp-lang-empty">
                {i18n.t("video_export.translate_no_languages", "Seslendirme için kayıtlı dil yok.")}
              </div>
            )}

            {/* Confirm step. Replaces the grid rather than stacking on top of
              it, so the choice being confirmed is the only thing on screen. */}
            {languages.length > 0 && pendingLanguage && (
              <div className="vexp-lang-confirm">
                <div className="vexp-lang-confirm-title">
                  {done.has(pendingLanguage)
                    ? i18n.t(
                        "video_export.translate_confirm_replace_title",
                        "Bu dil zaten var — yeniden üretilsin mi?"
                      )
                    : i18n.t("video_export.translate_confirm_title", "Bu dilde üretilsin mi?")}
                </div>
                <div className="vexp-lang-confirm-lang">
                  {langLabel(pendingLanguage)}{" "}
                  <span className="vexp-lang-chip-code">{pendingLanguage}</span>
                </div>
                <p className="vexp-lang-confirm-text">
                  {i18n.t(
                    "video_export.translate_confirm_text",
                    "Birleştirilmiş video bu dilde yeniden seslendirilecek. İşlem videonun uzunluğuna göre birkaç dakika sürebilir ve kredi harcar."
                  )}
                </p>
                <div className="vexp-lang-confirm-actions">
                  <button
                    type="button"
                    className="vexp-btn"
                    onClick={() => setPendingLanguage(null)}
                  >
                    {i18n.t("video_export.cancel", "İptal")}
                  </button>
                  <button
                    type="button"
                    className="vexp-btn primary"
                    disabled={!canGenerate}
                    onClick={() => onGenerate(pendingLanguage)}
                  >
                    <HiLanguage /> {i18n.t("video_export.translate_confirm_start", "Üret")}
                  </button>
                </div>
              </div>
            )}

            {languages.length > 0 && !pendingLanguage && (
              <>
                <div className="vexp-lang-grid">
                  {visibleLanguages.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      className={`vexp-lang-chip${done.has(lang) ? " done" : ""}`}
                      disabled={!canGenerate}
                      onClick={() => setPendingLanguage(lang)}
                      title={
                        canGenerate
                          ? done.has(lang)
                            ? i18n.t(
                                "video_export.translate_regenerate",
                                "Bu dil zaten var — yeniden üretilecek"
                              )
                            : i18n.t("video_export.translate_start", "Bu dilde üret")
                          : job
                            ? i18n.t("video_export.translate_busy", "Bir çeviri sürüyor")
                            : i18n.t(
                                "video_export.translate_disabled_tooltip",
                                "Önce videoyu birleştirin"
                              )
                      }
                    >
                      <span className="vexp-lang-chip-name">{langLabel(lang)}</span>
                      <span className="vexp-lang-chip-code">{lang}</span>
                      {done.has(lang) && (
                        <span className="vexp-lang-chip-badge">
                          {i18n.t("video_export.translate_exists", "mevcut")}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    className="vexp-lang-more"
                    onClick={() => setShowAllLanguages((prev) => !prev)}
                  >
                    {showAllLanguages
                      ? i18n.t("video_export.languages_show_less", "Daha az göster")
                      : i18n.t("video_export.languages_show_more", {
                          n: hiddenCount,
                          defaultValue: `+${hiddenCount} dil daha`,
                        })}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="vexp-start-actions">
          <button type="button" className="vexp-btn" onClick={onClose}>
            {i18n.t("video_export.close", "Kapat")}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Format a duration in seconds as `m:ss` (or `s s` under a minute). */
/**
 * "en-US" → "English (US)" in the current UI language, falling back to the
 * raw code. Intl.DisplayNames is unavailable in older embedded webviews and
 * throws on malformed tags, so both are guarded.
 */
const langLabel = (code) => {
  if (!code) return "";
  try {
    const names = new Intl.DisplayNames([i18n.language || "en"], { type: "language" });
    return names.of(code) || code;
  } catch {
    return code;
  }
};

const fmtDuration = (sec) => {
  if (sec == null || isNaN(sec)) return null;
  const s = Math.round(Number(sec));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
};

/**
 * Cross-origin-safe download. The native `<a download>` attribute is
 * silently ignored for cross-origin URLs — the browser just navigates
 * to the URL (which is what the user reported: "opens the player").
 *
 * Workaround: fetch the bytes, wrap in a Blob, create a same-origin
 * blob: URL, then trigger the download via a synthetic anchor click.
 * Works for any origin as long as the response is CORS-readable
 * (Azure Blob Storage allows GET cross-origin by default). Gated MW
 * `/api/f/:id` URLs need the session cookie, which credentialsFor(url) adds.
 *
 * Falls back to a plain navigation if fetch fails (better than nothing).
 */
async function triggerDownload(url, filename) {
  try {
    const res = await fetch(url, credentialsFor(url));
    if (!res.ok) throw new Error("HTTP " + res.status);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || urlToFilename(url);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Release after a tick so the click handler can resolve first.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (err) {
    console.warn("[VideoExportTabV2] blob download failed, falling back:", err);
    // Last-ditch: open in a new tab so we don't navigate the editor away.
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function urlToFilename(url) {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/");
    const last = parts[parts.length - 1] || "video.mp4";
    return last.includes(".") ? last : `${last}.mp4`;
  } catch (_) {
    return "video.mp4";
  }
}

const TURKISH_CHAR_MAP = {
  ç: "c",
  Ç: "c",
  ğ: "g",
  Ğ: "g",
  ı: "i",
  I: "i",
  İ: "i",
  ö: "o",
  Ö: "o",
  ş: "s",
  Ş: "s",
  ü: "u",
  Ü: "u",
};

// Turns a training title into a safe, ASCII, lowercase filename stem —
// e.g. "İngilizce Eğitim 1" -> "ingilizce-egitim-1".
function sanitizeFileNameStem(name) {
  if (!name) return "merged";
  const ascii = name.replace(/[çÇğĞıIİöÖşŞüÜ]/g, (ch) => TURKISH_CHAR_MAP[ch] ?? ch);
  const slug = ascii
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || "merged";
}

const SceneTile = ({
  index,
  scene,
  // Sahne adı (readLocalSceneTitles → API `scene.title`). Boş olabilir:
  // adsız sahnede yalnızca sıra numarası gösteriliyor.
  title,
  selected,
  isSkipped,
  isDragging,
  isDownloading,
  started,
  onSelect,
  onRegenerate,
  onCancel,
  onToggleSkipMerge,
  onDownload,
  onDragStart,
  onDragEnd,
  onDropOnTile,
}) => {
  const { navItemId, status, progress, videoUrl, thumbnailUrl, errorMessage, durationSec } = scene;
  const pct = Math.round((progress || 0) * 100);
  const dur = fmtDuration(durationSec);
  // Bu sahnenin oynatılabilir bir videosu var mı? Hariç tutmanın ANLAMI buna
  // bağlı: video yoksa sahne hiç üretilmeyecek, video varsa üretilmiş ama
  // birleştirmeye girmeyecek. Etiketler ve ipuçları bu ayrımı yansıtıyor.
  const produced = status === "completed" && !!videoUrl;
  // Only finished tiles are draggable. Dragging a still-rendering tile
  // doesn't make sense (no result to reorder yet) and would let the user
  // drop a placeholder into the middle of the strip mid-render.
  const draggable = status === "completed";
  return (
    <div
      className={
        "vexp-tile vexp-tile-" +
        status +
        (selected ? " is-selected" : "") +
        (isSkipped ? " is-skipped" : "") +
        (isDragging ? " is-dragging" : "")
      }
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.dataTransfer.effectAllowed = "move";
        // Required for Firefox to fire dragend reliably.
        try {
          e.dataTransfer.setData("text/plain", navItemId);
        } catch (_) {
          /* some browsers throw on certain types — ignore */
        }
        onDragStart && onDragStart();
      }}
      onDragOver={(e) => {
        // Without preventDefault, drop never fires.
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDropOnTile && onDropOnTile();
      }}
      onDragEnd={() => onDragEnd && onDragEnd()}
      onClick={onSelect}
    >
      <div className="vexp-tile-thumb">
        {videoUrl ? (
          <video src={videoUrl} muted preload="metadata" poster={thumbnailUrl || undefined} />
        ) : thumbnailUrl ? (
          <img src={thumbnailUrl} alt={title || navItemId} />
        ) : (
          <div className="vexp-tile-placeholder">{index}</div>
        )}
        {status === "rendering" && (
          <div className="vexp-tile-progress" style={{ width: pct + "%" }} />
        )}
        <div className="vexp-tile-index">{index}</div>
        {dur && <div className="vexp-tile-duration">{dur}</div>}
        {isSkipped && (
          <div className="vexp-tile-skipped-overlay" aria-hidden="true">
            <HiEyeSlash />
            <span>
              {produced
                ? i18n.t("video_export.dont_merge_overlay", "Birleştirilmeyecek")
                : i18n.t("video_export.dont_generate_overlay", "Oluşturulmayacak")}
            </span>
          </div>
        )}
      </div>
      {/* Sahne adı — durum rozeti/eylem düğmelerinin bulunduğu satırın
          ÜSTÜNDE kendi tam genişlikli satırında. O satıra sıkıştırılsa 3
          ikonla yarışıp iki-üç karakterde kırpılırdı. Uzun adlar tek satırda
          ellipsis'e düşer, tamamı `title` ipucunda görünür. */}
      {title && (
        <div className="vexp-tile-title" title={title}>
          {title}
        </div>
      )}
      <div className="vexp-tile-meta">
        {/* Export hiç başlatılmadıysa durum rozeti gösterilmiyor: o an her
            sahne "queued" olur ve "Sırada" yazısı, aslında sıraya girmemiş
            bir işi bekliyormuş gibi gösterir. */}
        {started && <span className={`vexp-status-pill ${status}`}>{translateStatus(status)}</span>}
        <div className="vexp-tile-actions">
          {/* Skip-merge toggle is available on EVERY scene state, not just
              `completed`. Otherwise a scene stuck on `rendering` (zombie
              job, worker died mid-render) blocks the merge gate forever
              with no escape — the user can't cancel (no worker listens)
              and can't exclude from merge. Letting the user mark any
              scene "Don't Merge" gives them a way out. */}
          {onToggleSkipMerge && (
            <button
              type="button"
              title={
                produced
                  ? isSkipped
                    ? i18n.t("video_export.dont_merge_tooltip_include", "Birleştirmeye dahil et")
                    : i18n.t("video_export.dont_merge_tooltip_exclude", "Birleştirmeye dahil etme")
                  : isSkipped
                    ? i18n.t("video_export.dont_generate_tooltip_include", "Üretime dahil et")
                    : i18n.t("video_export.dont_generate_tooltip_exclude", "Üretime dahil etme")
              }
              onClick={(e) => {
                e.stopPropagation();
                onToggleSkipMerge();
              }}
            >
              {/* İkon EYLEMİ değil DURUMU gösteriyor: dahil olan sahnede göz
                  açık, hariç tutulanda kapalı. Tersi okunuşu kafa karıştırıyordu
                  (her sahne kapalı gözle geliyormuş gibi görünüyordu). */}
              {isSkipped ? <HiEyeSlash /> : <HiEye />}
            </button>
          )}
          {/* Export başlamadan önce ne iptal edilecek bir iş ne de yeniden
              üretilecek bir video var — bu iki düğme yalnızca iş açıldıktan
              sonra anlamlı. */}
          {started &&
            (status === "rendering" || status === "queued" ? (
              <button
                type="button"
                title={i18n.t("video_export.cancel_scene_tooltip", "İptal")}
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel();
                }}
              >
                <HiStop />
              </button>
            ) : (
              <button
                type="button"
                title={i18n.t("video_export.regenerate_scene_tooltip", "Yeniden üret")}
                onClick={(e) => {
                  e.stopPropagation();
                  onRegenerate();
                }}
              >
                <HiArrowPath />
              </button>
            ))}
          {status === "completed" && videoUrl && (
            <button
              type="button"
              disabled={isDownloading}
              title={i18n.t("video_export.download", "İndir")}
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
            >
              {isDownloading ? <HiArrowPath className="spin" /> : <HiArrowDownTray />}
            </button>
          )}
        </div>
      </div>
      {errorMessage && (
        <div className="vexp-tile-error" title={errorMessage}>
          !
        </div>
      )}
    </div>
  );
};

/**
 * Dropdown that lists every prior merged-video for this training,
 * newest first. Reads from idealStudioFiles via the
 * /by-training/:tid/merges endpoint. Click an entry to load the URL
 * into the big preview; click the download icon to grab it directly.
 *
 * Closes on Escape and on click-outside (handled via a backdrop div
 * — cheaper than wiring a global listener inside the parent class).
 */
const MergeHistoryDropdown = ({ items, loading, onPick, onClose, downloadingKey, onDownload }) => {
  const list = Array.isArray(items) ? items : [];
  return (
    <>
      <div
        className="vexp-history-backdrop"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
      />
      <div className="vexp-history-panel" role="listbox" onClick={(e) => e.stopPropagation()}>
        <div className="vexp-history-header">
          {i18n.t("video_export.history_title", "Önceki birleştirmeler")}
        </div>
        {loading && (
          <div className="vexp-history-empty">
            <HiArrowPath className="spin" /> {i18n.t("video_export.history_loading", "Yükleniyor…")}
          </div>
        )}
        {!loading && list.length === 0 && (
          <div className="vexp-history-empty">
            {i18n.t("video_export.history_empty", "Henüz birleştirilmiş video yok.")}
          </div>
        )}
        {!loading &&
          list.map((it, i) => {
            const when = it.createdAt ? new Date(it.createdAt) : null;
            const whenLabel = when
              ? when.toLocaleString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—";
            const sizeMb = it.fileSize ? (it.fileSize / (1024 * 1024)).toFixed(1) : null;
            const dur = fmtDuration(it.durationSec);
            return (
              <div
                key={it.id}
                className="vexp-history-row"
                onClick={() => onPick(it)}
                role="option"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onPick(it);
                }}
              >
                <div className="vexp-history-row-main">
                  <span className="vexp-history-row-index">#{list.length - i}</span>
                  <span className="vexp-history-row-time">{whenLabel}</span>
                </div>
                <div className="vexp-history-row-meta">
                  {dur && <span>{dur}</span>}
                  {sizeMb && <span>{sizeMb} MB</span>}
                  <button
                    type="button"
                    className="vexp-history-row-dl"
                    title={i18n.t("video_export.download", "İndir")}
                    aria-label={i18n.t("video_export.download", "İndir")}
                    disabled={downloadingKey === `history:${it.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const ts = it.createdAt
                        ? new Date(it.createdAt).toISOString().replace(/[:.]/g, "-")
                        : Date.now();
                      onDownload(`history:${it.id}`, it.url, `video-export-${ts}.mp4`);
                    }}
                  >
                    {downloadingKey === `history:${it.id}` ? (
                      <HiArrowPath className="spin" aria-hidden="true" />
                    ) : (
                      <HiArrowDownTray aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};

const MergedTile = ({ selected, onSelect, url, isDownloading, onDownload }) => (
  <div
    className={`vexp-tile vexp-tile-completed vexp-tile-merged ${selected ? "is-selected" : ""}`}
    onClick={onSelect}
  >
    <div className="vexp-tile-thumb">
      <video src={url} muted preload="metadata" />
      <div className="vexp-tile-index">★</div>
    </div>
    <div className="vexp-tile-meta">
      <span className="vexp-status-pill completed">
        {i18n.t("video_export.status_merged", "Birleştirildi")}
      </span>
      <div className="vexp-tile-actions">
        <button
          type="button"
          disabled={isDownloading}
          title={i18n.t("video_export.download", "İndir")}
          onClick={(e) => {
            e.stopPropagation();
            onDownload();
          }}
        >
          {isDownloading ? <HiArrowPath className="spin" /> : <HiArrowDownTray />}
        </button>
      </div>
    </div>
  </div>
);

/**
 * Big-preview placeholder for the "RUNNING but no scene finished yet"
 * window — shimmering 16:9 box with a status caption. Better than a
 * bare "select a scene" message because the user knows render is on
 * the way; the tile strip above also pulses with queued/rendering pills.
 */
const PreviewSkeleton = ({ scenes }) => {
  const rendering = scenes.filter((s) => s.status === "rendering").length;
  const queued = scenes.filter((s) => s.status === "queued").length;
  // Always spell out the total. Showing only the in-flight count ("3 scene(s)
  // rendering") reads as if the whole export is just 3 scenes, when 3 is only
  // the concurrency slice of a much longer queue.
  const total = scenes.length;
  return (
    <div className="vexp-preview-skeleton">
      <div className="vexp-skeleton-frame">
        <div className="vexp-skeleton-shimmer" />
        <HiVideoCamera />
      </div>
      <div className="vexp-skeleton-caption">
        {i18n.t("video_export.preparing_scenes", "Sahneler hazırlanıyor…")}
        <small>
          {rendering > 0
            ? i18n.t("video_export.rendering_of_total", { count: rendering, total }) ||
              `${total} sahneden ${rendering} tanesi render ediliyor`
            : queued > 0
              ? i18n.t("video_export.queued_of_total", { count: queued, total }) ||
                `${total} sahneden ${queued} tanesi sırada bekliyor`
              : i18n.t("video_export.job_on_way", "İş yolda")}
        </small>
      </div>
    </div>
  );
};
/* eslint-enable react/prop-types */
